import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';
import KanbanConfigAPI from '../../api/kanbanConfig';


const state = {
  isEnabled: false,
  items: [],
  loading: false,
  error: null,
  config: {
    title: 'Gestor de Pedidos',
    defaultView: 'kanban',
    webhookUrl: '',
    webhookSecret: '',
    webhookEnabled: false,
    webhookEvents: [],
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
    dragbarEnabled: true,
    quickMessageEnabled: false,
  },
  pagination: {
    currentPage: 1,
    hasMore: true,
    loadingMore: false,
  },
};

const getters = {
  isKanbanEnabled: _state => _state.isEnabled,
  getKanbanItems: _state => _state.items,
  isLoading: _state => _state.loading,
  getError: _state => _state.error,
  getKanbanConfig: _state => _state.config,
  getKanbanTitle: _state => _state.config?.title || 'Gestor de Pedidos',
  getWebhookUrl: _state => _state.config?.webhookUrl || '',
  getWebhookSecret: _state => _state.config?.webhookSecret || '',
  getWebhookEnabled: _state => _state.config?.webhookEnabled || false,
  getWebhookEvents: _state => _state.config?.webhookEvents || [],
  getSupportEmail: _state => _state.config?.supportEmail || '',
  getSupportPhone: _state => _state.config?.supportPhone || '',
  getSupportChatHours: _state => _state.config?.supportChatHours || '',
  getSupportChatEnabled: _state => _state.config?.supportChatEnabled || false,
  getDragbarEnabled: _state => _state.config?.dragbarEnabled !== false,
  getQuickMessageEnabled: _state => _state.config?.quickMessageEnabled || false,
  getPaginationInfo: _state => _state.pagination,
  hasMorePages: _state => _state.pagination.hasMore,
  isLoadingMore: _state => _state.pagination.loadingMore,
};

const actions = {
  setKanbanEnabled({ commit }, isEnabled) {
    commit(types.SET_KANBAN_ENABLED, isEnabled);
  },

  async fetchKanbanItems({ commit, state: localState }, { force = false } = {}) {
    console.log('[STORE-DEBUG] fetchKanbanItems iniciado');
    commit('SET_LOADING', true);
    commit('RESET_PAGINATION'); // Reset pagination when fetching fresh data
    try {
      const currentFunnel = this.getters['funnel/getSelectedFunnel'];
      if (!currentFunnel?.id) {
        throw new Error('Nenhum funil selecionado');
      }

      console.log(
        '[STORE-DEBUG] Buscando itens para o funil:',
        currentFunnel.id
      );
      const response = await KanbanAPI.getItems(currentFunnel.id, 1);

      // Verificar se a resposta tem a nova estrutura
      const items = response.data.items || response.data;
      const pagination = response.data.pagination;

      console.log(
        '[STORE-DEBUG] API getItems respondeu com',
        items.length,
        'itens'
      );

      commit('SET_KANBAN_ITEMS', items);

      if (pagination) {
        commit('SET_PAGINATION_INFO', {
          currentPage: pagination.current_page,
          hasMore: pagination.has_more,
        });
      } else {
        // Fallback para estrutura antiga
        commit('SET_PAGINATION_INFO', {
          currentPage: 1,
          hasMore: items.length === 50, // Se retornou 50 itens, provavelmente há mais
        });
      }

      console.log(
        '[STORE-DEBUG] fetchKanbanItems - Store atualizado com novos itens:',
        localState.items.length
      );
      return items;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro ao buscar itens:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchMoreKanbanItems({ commit, state: localState }) {
    console.log('[STORE-DEBUG] fetchMoreKanbanItems chamado - hasMore:', localState.pagination.hasMore, 'loadingMore:', localState.pagination.loadingMore);

    if (!localState.pagination.hasMore || localState.pagination.loadingMore) {
      console.log('[STORE-DEBUG] fetchMoreKanbanItems ignorado - hasMore:', localState.pagination.hasMore, 'loadingMore:', localState.pagination.loadingMore);
      return;
    }

    commit('SET_LOADING_MORE', true);
    try {
      const currentFunnel = this.getters['funnel/getSelectedFunnel'];
      if (!currentFunnel?.id) {
        throw new Error('Nenhum funil selecionado');
      }

      const nextPage = localState.pagination.currentPage + 1;
      console.log('[STORE-DEBUG] Carregando página', nextPage, 'para o funil:', currentFunnel.id);
      console.log('[STORE-DEBUG] Itens atuais no store:', localState.items.length);

      const response = await KanbanAPI.getItems(currentFunnel.id, nextPage);

      // Verificar se a resposta tem a nova estrutura
      const items = response.data.items || response.data;
      const pagination = response.data.pagination;

      console.log('[STORE-DEBUG] API getItems página', nextPage, 'respondeu com', items.length, 'itens');

      if (items.length > 0) {
        // Verificar se há itens duplicados antes de adicionar
        const existingIds = new Set(localState.items.map(item => item.id));
        const newItems = items.filter(item => !existingIds.has(item.id));

        console.log('[STORE-DEBUG] Itens únicos para adicionar:', newItems.length, 'de', items.length);

        if (newItems.length > 0) {
          commit('APPEND_KANBAN_ITEMS', newItems);
        }

        if (pagination) {
          commit('SET_PAGINATION_INFO', {
            currentPage: pagination.current_page,
            hasMore: pagination.has_more,
          });
        } else {
          // Fallback para estrutura antiga
          commit('SET_PAGINATION_INFO', {
            currentPage: nextPage,
            hasMore: items.length === 50, // Se retornou 50 itens, provavelmente há mais
          });
        }

        console.log('[STORE-DEBUG] Paginação atualizada - currentPage:', pagination?.current_page || nextPage, 'hasMore:', pagination?.has_more || items.length === 50);
      } else {
        commit('SET_PAGINATION_INFO', {
          currentPage: localState.pagination.currentPage,
          hasMore: false,
        });
        console.log('[STORE-DEBUG] Nenhum item retornado, hasMore definido como false');
      }

      return items;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em fetchMoreKanbanItems:', error);
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING_MORE', false);
      console.log('[STORE-DEBUG] fetchMoreKanbanItems finalizado');
    }
  },

  async createKanbanItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.createItem(payload);
      commit('ADD_KANBAN_ITEM', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async moveItemToStage({ commit, dispatch }, { itemId, columnId, funnelId = null }) {
    console.log(
      '[MOVE-STORE-DEBUG] moveItemToStage iniciado - itemId:',
      itemId,
      'columnId:',
      columnId,
      'funnelId:',
      funnelId
    );

    // Garantir que itemId seja número
    const numericItemId = parseInt(itemId, 10);

    // Obter o item original para possível reversão
    const originalItem = state.items.find(i => i.id === numericItemId);
    // console.log(
    //   '[MOVE-STORE-DEBUG] Item encontrado?',
    //   !!originalItem,
    //   'ID numérico:',
    //   numericItemId
    // );
    // console.log(
    //   '[MOVE-STORE-DEBUG] IDs no store:',
    //   state.items.map(i => i.id)
    // );

    if (originalItem) {
      // Modificar o item no store antes da API (igual à exclusão)
      commit('UPDATE_ITEM_STAGE', { itemId: numericItemId, columnId });
    }

    try {
      // Chamar a API, passando funnelId se houver
      await KanbanAPI.moveToStage(numericItemId, columnId, funnelId);
      // Item movido com sucesso

      // Atualizar stats do funnel após mover item
      console.log('[MOVE-STORE-DEBUG] funnelId recebido:', funnelId);
      if (funnelId) {
        console.log('[MOVE-STORE-DEBUG] Atualizando stats para funnelId:', funnelId);
        await dispatch('funnel/fetchStageStats', funnelId, { root: true });
        console.log('[MOVE-STORE-DEBUG] Stats atualizados com sucesso');
      } else {
        console.warn('[MOVE-STORE-DEBUG] funnelId não recebido, não foi possível atualizar stats');
      }

      // Mensagens automáticas agora são tratadas pelo backend via KanbanTemplateMessageHandler
      return true;
    } catch (error) {
      console.error('[MOVE-STORE-DEBUG] Erro na API ao mover item:', error);
      // Em caso de erro, reverter para o estado original
      if (originalItem) {
        commit('UPDATE_ITEM_STAGE', {
          itemId: numericItemId,
          columnId: originalItem.funnel_stage,
        });
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async deleteKanbanItem({ commit }, itemId) {
    const originalItem = state.items.find(i => i.id === itemId);
    commit('REMOVE_KANBAN_ITEM', itemId);

    try {
      await KanbanAPI.deleteItem(itemId);
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_KANBAN_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.updateItemStructure(itemId, {
        item: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_structure,
      });
      // Item atualizado com sucesso
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateItemDetails({ commit, dispatch }, { itemId, details }) {
    console.log(`[STORE-DEBUG] updateItemDetails iniciado - Item ID: ${itemId}, Detalhes:`, details);
    // --- Otimização: Atualizar o store localmente primeiro para UI mais rápida --- //
    commit('UPDATE_ITEM_DETAILS', { itemId, details });
    console.log(`[STORE-DEBUG] updateItemDetails - Store local atualizado (antes da API)`);

    try {
      console.log(`[STORE-DEBUG] updateItemDetails - Chamando API.getItem para ID: ${itemId}`);
      const currentItem = await KanbanAPI.getItem(itemId);
      console.log(`[STORE-DEBUG] updateItemDetails - Dados atuais da API:`, currentItem.data);
      const payload = {
        funnel_id: details.funnel_id ?? currentItem.data.funnel_id,
        funnel_stage: details.funnel_stage ?? currentItem.data.funnel_stage,
        position: details.position ?? currentItem.data.position,
        item_details: details.item_details ?? currentItem.data.item_details,
        conversation_display_id: details.conversation_display_id ?? currentItem.data.conversation_display_id,
        timer_started_at: details.timer_started_at ?? currentItem.data.timer_started_at,
        timer_duration: details.timer_duration ?? currentItem.data.timer_duration,
        custom_attributes: details.custom_attributes ?? currentItem.data.custom_attributes,
      };
      console.log(`[STORE-DEBUG] updateItemDetails - Payload para API.updateItem:`, payload);
      const response = await KanbanAPI.updateItem(itemId, payload);
      console.log(`[STORE-DEBUG] updateItemDetails - Resposta da API.updateItem:`, response.data);

      // --- Garantir que o store local reflete a resposta da API --- //
      // commit('UPDATE_ITEM_DETAILS', { itemId, details: response.data }); // Descomente se necessário sincronizar com a resposta exata
      // console.log(`[STORE-DEBUG] updateItemDetails - Store local atualizado com resposta da API`);

      // Item atualizado com sucesso
      console.log(`[STORE-DEBUG] updateItemDetails - Item atualizado com sucesso`);
      return response.data;
    } catch (error) {
      console.error('[STORE-DEBUG] Erro em updateItemDetails:', error);
      // TODO: Reverter a mutação local em caso de erro?
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async registerActivity({ commit, dispatch }, { itemId, type, details }) {
    try {
      const currentItem = await KanbanAPI.getItem(itemId);
      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: {
          id: this.getters.getCurrentUser.id,
          name: this.getters.getCurrentUser.name,
          avatar_url: this.getters.getCurrentUser.avatar_url,
        },
      };

      const activities = [
        ...(currentItem.data.item_details?.activities || []),
        newActivity,
      ];

      const payload = {
        ...currentItem.data,
        item_details: {
          ...currentItem.data.item_details,
          activities,
        },
      };

      const response = await KanbanAPI.updateItem(itemId, payload);
      // Atividade registrada com sucesso
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async fetchKanbanConfig({ commit }) {
    try {
      const response = await KanbanConfigAPI.getConfig();

      const config = {
        title: response.data.config?.title || 'Gestor de Pedidos',
        defaultView: response.data.config?.default_view || 'kanban',
        webhookUrl: response.data.webhook_url || '',
        webhookSecret: response.data.webhook_secret || '',
        webhookEnabled: response.data.enabled || false,
        webhookEvents: response.data.webhook_events || [],
        autoAssignment: response.data.config?.auto_assignment || false,
        notificationsEnabled: response.data.config?.notifications_enabled || false,
        supportEmail: response.data.config?.support_email || '',
        supportPhone: response.data.config?.support_phone || '',
        supportChatHours: response.data.config?.support_chat_hours || '',
        supportChatEnabled: response.data.config?.support_chat_enabled || false,
        dragbarEnabled: response.data.config?.dragbar_enabled !== false,
        quickMessageEnabled: response.data.config?.quick_message_enabled || false,
      };

      commit('SET_KANBAN_CONFIG', config);
      return config;
    } catch (error) {
      console.warn('Erro ao carregar configurações do Kanban:', error);
      const defaultConfig = {
        title: 'Gestor de Pedidos',
        defaultView: 'kanban',
        webhookUrl: '',
        webhookSecret: '',
        webhookEnabled: false,
        webhookEvents: [],
        autoAssignment: false,
        notificationsEnabled: false,
        supportEmail: '',
        supportPhone: '',
        supportChatHours: '',
        supportChatEnabled: false,
        dragbarEnabled: true,
        quickMessageEnabled: false,
      };
      commit('SET_KANBAN_CONFIG', defaultConfig);
      return defaultConfig;
    }
  },

  updateLocalItems({ commit }, items) {
    commit('SET_KANBAN_ITEMS', items);
  },

  async saveKanbanConfig({ commit, dispatch }, configData) {
    try {
      const response = await KanbanConfigAPI.saveConfig(configData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      console.error('Erro ao salvar configurações do Kanban:', error);
      throw error;
    }
  },

  async updateWebhookConfig({ commit, dispatch }, webhookData) {
    try {
      const response = await KanbanConfigAPI.updateWebhookConfig(webhookData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      console.error('Erro ao atualizar configurações de webhook:', error);
      throw error;
    }
  },

  async testWebhook({ commit }) {
    try {
      const response = await KanbanConfigAPI.testWebhook();
      return response;
    } catch (error) {
      console.error('Erro ao testar webhook:', error);
      throw error;
    }
  },

  async updateQuickMessageConfig({ commit, dispatch, state: localState }, enabled) {
    try {
      // Obter a configuração atual do store
      const currentConfig = { ...localState.config };

      // Fazer merge da nova configuração com a existente
      const updatedConfig = {
        ...currentConfig,
        quick_message_enabled: enabled
      };

      const response = await KanbanConfigAPI.updateConfig({
        config: updatedConfig
      });

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      console.error('Erro ao atualizar configuração de mensagem rápida:', error);
      throw error;
    }
  },


};

const mutations = {
  [types.SET_KANBAN_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },
  SET_KANBAN_ITEMS(_state, items) {
    _state.items = items;
  },
  ADD_KANBAN_ITEM(_state, item) {
    _state.items.push(item);
  },
  REMOVE_KANBAN_ITEM(_state, itemId) {
    _state.items = _state.items.filter(item => item.id !== itemId);
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    console.log(
      '[MUTATION-DEBUG] UPDATE_ITEM_STAGE chamada - itemId:',
      itemId,
      'columnId:',
      columnId
    );

    // Garantir que o itemId seja um número
    const numericItemId = parseInt(itemId, 10);
    console.log(
      '[MUTATION-DEBUG] Tipo do itemId:',
      typeof itemId,
      'Valor numérico:',
      numericItemId
    );

    // Buscar o item usando o ID numérico
    const item = _state.items.find(i => i.id === numericItemId);
    console.log(
      '[MUTATION-DEBUG] Item encontrado?',
      !!item,
      'IDs no store:',
      _state.items.map(i => i.id)
    );

    // Atualizar o item se encontrado
    if (item) {
      console.log(
        '[MUTATION-DEBUG] Atualizando item:',
        item.id,
        'de',
        item.funnel_stage,
        'para',
        columnId
      );
      item.funnel_stage = columnId;
      console.log('[MUTATION-DEBUG] Após atualização:', item.funnel_stage);
    } else {
      console.log('[MUTATION-DEBUG] Item não encontrado! ID:', numericItemId);
    }
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const item = _state.items.find(i => i.id === itemId);
    if (item) {
      item.item_structure = structure;
    }
  },
  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    console.log(`[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item ID: ${itemId}, Detalhes:`, details);
    const item = _state.items.find(i => i.id === itemId);
    if (item) {
      console.log('[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item encontrado, atualizando...');
      // Atualizar todos os campos relevantes que estão nos 'details'
      // Garantir que apenas as propriedades existentes em 'details' sejam atualizadas
      Object.keys(details).forEach(key => {
        if (key in item) {
          item[key] = details[key];
        } else if (key === 'item_details') {
          item.item_details = {
            ...item.item_details, // Mantém detalhes existentes
            ...details.item_details, // Sobrescreve com novos detalhes
          };
        }
      });
      console.log('[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item após atualização:', item);
    } else {
      console.warn(`[STORE-MUTATION-DEBUG] UPDATE_ITEM_DETAILS - Item com ID ${itemId} não encontrado no store.`);
    }
  },
  SET_KANBAN_CONFIG(_state, config) {
    _state.config = config;
  },

  RESET_PAGINATION(_state) {
    _state.pagination = {
      currentPage: 1,
      hasMore: true,
      loadingMore: false,
    };
  },
  SET_PAGINATION_INFO(_state, { currentPage, hasMore }) {
    _state.pagination.currentPage = currentPage;
    _state.pagination.hasMore = hasMore;
  },
  SET_LOADING_MORE(_state, loading) {
    _state.pagination.loadingMore = loading;
  },
  APPEND_KANBAN_ITEMS(_state, items) {
    // Filtrar itens que já existem no store para evitar duplicatas
    const existingIds = new Set(_state.items.map(item => item.id));
    const newItems = items.filter(item => !existingIds.has(item.id));

    if (newItems.length > 0) {
      console.log('[STORE-DEBUG] Adicionando', newItems.length, 'novos itens ao store');
      _state.items.push(...newItems);
    } else {
      console.log('[STORE-DEBUG] Nenhum item novo para adicionar (todos já existem)');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
